import { getSubscription } from './subscriptionService';
import { getUserUsage } from './userRecordsService';
import { FEATURE_LIMITS } from '../constants/featureLimits';
import { LimitCheckResult, FeatureName } from '../types/limits';

export const checkLimit = async (
  feature: FeatureName,
  userId: string
): Promise<LimitCheckResult> => {
  try {
    const [subscription, usage] = await Promise.all([
      getSubscription(userId),
      getUserUsage(userId)
    ]);

    if (!subscription) {
      return { allowed: false, message: 'Erro ao verificar assinatura' };
    }

    if (!usage) {
      return { allowed: true, remaining: 1, limit: 1 };
    }

    const limits = FEATURE_LIMITS[subscription.plan] || FEATURE_LIMITS.free;
    const featureLimit = limits[getFeatureLimitKey(feature)];

    // -1 significa ilimitado
    if (featureLimit === -1) {
      return { allowed: true, remaining: -1, limit: -1 };
    }

    const currentUsage = getCurrentUsage(feature, usage);
    const allowed = currentUsage < featureLimit;
    const remaining = Math.max(0, featureLimit - currentUsage);

    return {
      allowed,
      remaining,
      limit: featureLimit,
      featureName: feature,
      message: allowed
        ? undefined
        : `Limite de ${featureLimit} ${getFeatureDisplayName(feature)} atingido. Faça upgrade para continuar!`
    };
  } catch (error) {
    console.error('Erro ao verificar limite:', error);
    // Em caso de erro, permitir mas logar
    return { allowed: true, message: 'Erro ao verificar limites, permitindo ação' };
  }
};

export const canCreateEvent = async (userId: string): Promise<LimitCheckResult> => {
  return checkLimit('events', userId);
};

export const canAddGuest = async (userId: string): Promise<LimitCheckResult> => {
  return checkLimit('guests', userId);
};

export const canAddRecipe = async (userId: string): Promise<LimitCheckResult> => {
  return checkLimit('recipes', userId);
};

export const canAddInventoryItem = async (userId: string): Promise<LimitCheckResult> => {
  return checkLimit('inventory', userId);
};

export const canCreateGuestGroup = async (userId: string): Promise<LimitCheckResult> => {
  return checkLimit('guestGroups', userId);
};

export const canCreateShoppingList = async (userId: string): Promise<LimitCheckResult> => {
  return checkLimit('shoppingLists', userId);
};

export const canAddWishlistItem = async (userId: string): Promise<LimitCheckResult> => {
  return checkLimit('wishlist', userId);
};

const getFeatureLimitKey = (feature: FeatureName): keyof typeof FEATURE_LIMITS.free => {
  const mapping = {
    events: 'eventsPerMonth',
    guests: 'totalGuests',
    recipes: 'totalRecipes',
    inventory: 'totalInventoryItems',
    guestGroups: 'guestGroups',
    shoppingLists: 'activeShoppingLists',
    wishlist: 'wishlistItems',
    categories: 'customCategories',
    photos: 'photosPerEvent',
    videos: 'videosPerEvent',
  } as const;

  return mapping[feature];
};

const getCurrentUsage = (feature: FeatureName, usage: any): number => {
  const mapping = {
    events: usage.eventsThisMonth,
    guests: usage.totalGuests,
    recipes: usage.totalRecipes,
    inventory: usage.totalInventoryItems,
    guestGroups: usage.guestGroups,
    shoppingLists: usage.activeShoppingLists,
    wishlist: usage.wishlistItems,
    categories: usage.customCategories,
    photos: 0,
    videos: 0,
  };

  return mapping[feature] || 0;
};

const getFeatureDisplayName = (feature: FeatureName): string => {
  const mapping = {
    events: 'eventos',
    guests: 'convidados',
    recipes: 'receitas',
    inventory: 'itens no acervo',
    guestGroups: 'grupos de convidados',
    shoppingLists: 'listas de compras',
    wishlist: 'itens na wishlist',
    categories: 'categorias personalizadas',
    photos: 'fotos',
    videos: 'vídeos',
  };

  return mapping[feature];
};
